import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Database, RefreshCw } from "lucide-react";

export function HubSpotTab() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/hubspot/lookup", { params: { email: email.trim() } });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lookup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-hubspot">
      <Card className="p-4 rounded-2xl border-border bg-navy/5">
        <p className="text-sm text-navy">Debug tool: enter an email to see the raw and mapped data MyElite retrieves from HubSpot for registration prefill.</p>
      </Card>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="hubspot-email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="student@example.com" className="pl-9 h-11 rounded-xl" />
        </div>
        <Button data-testid="hubspot-lookup" onClick={lookup} disabled={loading} className="rounded-full bg-navy hover:bg-navy-deep text-white h-11">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Database className="h-4 w-4 mr-1" /> Lookup</>}
        </Button>
      </div>

      {result && (
        <Card className="p-6 rounded-2xl border-border" data-testid="hubspot-result">
          {result.error ? (
            <p className="text-sm text-destructive">Error: {result.error}</p>
          ) : !result.found ? (
            <p className="text-sm text-muted-foreground">No HubSpot contact found for this email (the student would see the Typeform intake at signup).</p>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">Found</Badge>
                <span className="text-xs text-muted-foreground">Contact ID: {result.contact_id}</span>
                <Badge variant="secondary" className="capitalize">Mapped level: {result.mapped?.persona}</Badge>
              </div>
              <div>
                <div className="eyebrow text-[11px] text-orange mb-2">Mapped profile (used for prefill)</div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> <b className="text-navy">{result.mapped?.name || "—"}</b></div>
                  <div><span className="text-muted-foreground">Student type:</span> <b className="text-navy capitalize">{result.mapped?.persona}</b></div>
                  {Object.entries(result.mapped?.profile || {}).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> <b className="text-navy">{v || "—"}</b></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow text-[11px] text-orange mb-2">Raw HubSpot properties</div>
                <pre className="text-xs bg-navy text-white/90 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap font-mono">{JSON.stringify(result.properties, null, 2)}</pre>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
